import React, { useState } from "react";

// ── Default payloads sent to API ─────────────────────────────────────────────
const VEHICLE_API_DEFAULTS = {
  transporter_id: 1,
  registration_number: 0,
  model: "N/A",
  manufacturer: "N/A",
  year: 2000,
  capacity_kg: 0,
  is_available: true,
  fuel_type: "diesel ",
  fitness_expiry: new Date().toISOString().slice(0, 10),
  insurance_expiry: new Date().toISOString().slice(0, 10),
  color: "N/A",
  vehicle_type: "N/A",
};

const DRIVER_API_DEFAULTS = {
  user_id: 1,
  transporter_id: 1,
  name: "",
  license_number: new Date().getSeconds().toString(),
  license_expiry: new Date().toISOString().slice(0, 10),
  phone: "",
  current_vehicle_id: null,
  status: "available",
  aadhar: null,
  address: null,
  city: null,
  state: null,
  upi_id: null,
  bank_account_name: null,
  bank_account_number: null,
  bank_ifsc: null,
  bank_name: null,
};

const API_URL = process.env.REACT_APP_API_URL ?? "";

// ── Shared style helpers ─────────────────────────────────────────────────────
const labelClass =
  "block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5";
const inputClass =
  "border border-slate-200 rounded-lg px-4 py-2.5 text-sm w-full bg-white " +
  "focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition placeholder:text-slate-300";
const cardClass =
  "bg-white border border-slate-100 rounded-2xl shadow-sm p-6 mb-6";

// ── Vehicle Section ───────────────────────────────────────────────────────────
const VehicleSection: React.FC = () => {
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [vehicles, setVehicles] = useState<
    { vehicle_number: string; id: number; driver_name?: string; driver_phone?: string }[]
  >([]);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editVehicleNumber, setEditVehicleNumber] = useState("");
  const [editDriverName, setEditDriverName] = useState("");
  const [editDriverPhone, setEditDriverPhone] = useState("");
  const [loading, setLoading] = useState(false);

  // Add vehicle (no driver, as the vehicle is separate concept in add; driver assigned in driver section)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vehicleNumber.trim()) {
      alert("Vehicle number is required.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...VEHICLE_API_DEFAULTS,
        vehicle_number: vehicleNumber.trim(),
      };
      const resp = await fetch(`${API_URL}/auth/vehicles`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        let msg = "Failed to add vehicle.";
        try {
          const errBody = await resp.json();
          if (errBody?.error) msg = errBody.error;
        } catch {}
        throw new Error(msg);
      }
      const data = await resp.json();
      if (!data?.id) throw new Error("No vehicle id from backend.");
      setVehicles((prev) => [
        ...prev,
        { vehicle_number: vehicleNumber.trim(), id: data.id },
      ]);
      setVehicleNumber("");
    } catch (err: any) {
      alert(err?.message || "Could not save vehicle.");
    } finally {
      setLoading(false);
    }
  };

  // Edit logic: opens edit form for a vehicle
  const handleEdit = (index: number) => {
    setEditIndex(index);
    setEditVehicleNumber(vehicles[index].vehicle_number);
    setEditDriverName(vehicles[index].driver_name || "");
    setEditDriverPhone(vehicles[index].driver_phone || "");
  };

  // Save edits (local only for now, unless API endpoint for update)
  const handleSaveEdit = () => {
    if (!editVehicleNumber.trim()) {
      alert("Vehicle number is required.");
      return;
    }
    setVehicles((prev) =>
      prev.map((v, i) =>
        i === editIndex
          ? {
              ...v,
              vehicle_number: editVehicleNumber.trim(),
              driver_name: editDriverName.trim(),
              driver_phone: editDriverPhone.trim(),
            }
          : v
      )
    );
    setEditIndex(null);
    setEditVehicleNumber("");
    setEditDriverName("");
    setEditDriverPhone("");
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setEditIndex(null);
    setEditVehicleNumber("");
    setEditDriverName("");
    setEditDriverPhone("");
  };

  // Delete logic (local only; for real app call delete endpoint)
  const handleDelete = (index: number) => {
    if (window.confirm("Are you sure you want to delete this vehicle?")) {
      setVehicles((prev) => prev.filter((_, i) => i !== index));
    }
  };

  return (
    <div className={cardClass}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center text-white text-lg">
          🚛
        </div>
        <div>
          <h3 className="font-bold text-lg text-slate-800 leading-tight">Add Vehicle</h3>
          <p className="text-xs text-slate-400">Register a vehicle to the system</p>
        </div>
      </div>

      {/* Vehicles list — just show vehicle number, driver name and driver number */}
      {vehicles.length > 0 && (
        <div className="mb-5 rounded-xl bg-indigo-50 border border-indigo-100 overflow-hidden">
          <div className="px-4 py-2 bg-indigo-100 text-xs font-semibold uppercase tracking-widest text-indigo-600">
            Vehicles List ({vehicles.length})
          </div>
          <ul className="divide-y divide-indigo-100">
            {vehicles.map((v, i) =>
              editIndex === i ? (
                <li
                  key={i}
                  className="space-y-1 px-4 py-2.5 flex flex-col md:flex-row items-start md:items-center md:justify-between gap-2 bg-slate-50"
                >
                  <div className="flex-1 grid md:grid-cols-3 gap-2 w-full">
                    <input
                      className={inputClass}
                      placeholder="Vehicle Number"
                      value={editVehicleNumber}
                      onChange={(e) => setEditVehicleNumber(e.target.value)}
                      required
                    />
                    <input
                      className={inputClass}
                      placeholder="Driver Name (optional)"
                      value={editDriverName}
                      onChange={(e) => setEditDriverName(e.target.value)}
                    />
                    <input
                      className={inputClass}
                      placeholder="Driver Number (optional)"
                      value={editDriverPhone}
                      onChange={(e) => setEditDriverPhone(e.target.value)}
                    />
                  </div>
                  <div className="flex gap-1 mt-2 md:mt-0">
                    <button
                      type="button"
                      className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-md px-2 py-1 text-xs font-semibold"
                      onClick={handleSaveEdit}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-md px-2 py-1 text-xs font-semibold"
                      onClick={handleCancelEdit}
                    >
                      Cancel
                    </button>
                  </div>
                </li>
              ) : (
                <li
                  key={i}
                  className="px-4 py-2.5 flex flex-col md:flex-row items-start md:items-center justify-between gap-2"
                >
                  <span className="font-semibold text-indigo-700 text-sm">
                    {v.vehicle_number}
                  </span>
                  <span className="font-semibold text-slate-600 text-sm">
                    {v.driver_name || "-"}
                  </span>
                  <span className="font-semibold text-slate-500 text-sm">
                    {v.driver_phone || "-"}
                  </span>
                  {/* No edit/delete if listb, but keeping as before since no new info given */}
                  <span className="flex gap-1">
                    <button
                      type="button"
                      className="bg-indigo-100 hover:bg-indigo-200 text-indigo-800 rounded-lg px-2 py-1 text-xs font-semibold mr-1"
                      onClick={() => handleEdit(i)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="bg-red-100 hover:bg-red-200 text-red-700 rounded-lg px-2 py-1 text-xs font-semibold"
                      onClick={() => handleDelete(i)}
                    >
                      Delete
                    </button>
                  </span>
                </li>
              )
            )}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div>
          <label className={labelClass}>Vehicle Number *</label>
          <input
            className={inputClass}
            placeholder="e.g. VH-001"
            value={vehicleNumber}
            onChange={(e) => {
              setVehicleNumber(e.target.value);
            }}
            required
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="mt-5 w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition disabled:opacity-60 text-sm tracking-wide"
        >
          {loading ? "Saving…" : "+ Add Vehicle"}
        </button>
      </form>
    </div>
  );
};

// ── Driver Section ────────────────────────────────────────────────────────────
const DriverSection: React.FC = () => {
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [drivers, setDrivers] = useState<
    { phone: string; id: number; name: string }[]
  >([]);
  const [loading, setLoading] = useState(false);

  // For edit driver
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim()) {
      alert("Driver phone number is required.");
      return;
    }
    if (!name.trim()) {
      alert("Driver name is required.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...DRIVER_API_DEFAULTS,
        phone: phone.trim(),
        name: name.trim(),
      };
      const resp = await fetch(`${API_URL}/auth/drivers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        let msg = "Failed to create driver.";
        try {
          const e = await resp.json();
          if (e?.error) msg = e.error;
        } catch {}
        throw new Error(msg);
      }
      const data = await resp.json();
      if (!data?.id) throw new Error("No driver id from backend.");
      setDrivers((prev) => [
        ...prev,
        { phone: phone.trim(), name: name.trim(), id: data.id },
      ]);
      setPhone("");
      setName("");
    } catch (err: any) {
      alert(err?.message || "Could not save driver.");
    } finally {
      setLoading(false);
    }
  };

  // Edit: open edit UI
  const handleEdit = (index: number) => {
    setEditIndex(index);
    setEditName(drivers[index].name);
    setEditPhone(drivers[index].phone);
  };

  // Save edit in UI (this is local only)
  const handleSaveEdit = () => {
    if (!editPhone.trim()) {
      alert("Driver phone number is required.");
      return;
    }
    if (!editName.trim()) {
      alert("Driver name is required.");
      return;
    }
    setDrivers((prev) =>
      prev.map((d, i) =>
        i === editIndex
          ? { ...d, name: editName.trim(), phone: editPhone.trim() }
          : d
      )
    );
    setEditIndex(null);
    setEditName("");
    setEditPhone("");
  };

  const handleCancelEdit = () => {
    setEditIndex(null);
    setEditName("");
    setEditPhone("");
  };

  // Delete
  const handleDelete = (index: number) => {
    if (window.confirm("Are you sure you want to delete this driver?")) {
      setDrivers((prev) => prev.filter((_, i) => i !== index));
    }
  };

  return (
    <div className={cardClass}>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-xl bg-emerald-600 flex items-center justify-center text-white text-lg">
          👤
        </div>
        <div>
          <h3 className="font-bold text-lg text-slate-800 leading-tight">
            Add Driver
          </h3>
          <p className="text-xs text-slate-400">
            Register a driver to the system
          </p>
        </div>
      </div>

      {/* Drivers list — just show driver name and driver number */}
      {drivers.length > 0 && (
        <div className="mb-5 rounded-xl bg-emerald-50 border border-emerald-100 overflow-hidden">
          <div className="px-4 py-2 bg-emerald-100 text-xs font-semibold uppercase tracking-widest text-emerald-700">
            Drivers List ({drivers.length})
          </div>
          <ul className="divide-y divide-emerald-100">
            {drivers.map((d, i) =>
              editIndex === i ? (
                <li
                  key={i}
                  className="px-4 py-2.5 flex flex-col md:flex-row items-start md:items-center justify-between gap-2 bg-slate-50"
                >
                  <div className="flex-1 grid md:grid-cols-2 gap-2 w-full">
                    <input
                      className={inputClass}
                      placeholder="Driver Name"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      required
                    />
                    <input
                      className={inputClass}
                      placeholder="Driver Phone"
                      value={editPhone}
                      onChange={(e) => setEditPhone(e.target.value)}
                      required
                    />
                  </div>
                  <div className="flex gap-1 mt-2 md:mt-0">
                    <button
                      type="button"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-md px-2 py-1 text-xs font-semibold"
                      onClick={handleSaveEdit}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-md px-2 py-1 text-xs font-semibold"
                      onClick={handleCancelEdit}
                    >
                      Cancel
                    </button>
                  </div>
                </li>
              ) : (
                <li
                  key={i}
                  className="px-4 py-2.5 flex flex-col md:flex-row items-start md:items-center justify-between gap-2"
                >
                  <span className="font-semibold text-emerald-700 text-sm">
                    {d.name}
                  </span>
                  <span className="font-semibold text-slate-500 text-sm">
                    {d.phone}
                  </span>
                  <span className="flex gap-1">
                    <button
                      type="button"
                      className="bg-emerald-100 hover:bg-emerald-200 text-emerald-800 rounded-lg px-2 py-1 text-xs font-semibold mr-1"
                      onClick={() => handleEdit(i)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="bg-red-100 hover:bg-red-200 text-red-700 rounded-lg px-2 py-1 text-xs font-semibold"
                      onClick={() => handleDelete(i)}
                    >
                      Delete
                    </button>
                  </span>
                </li>
              )
            )}
          </ul>
        </div>
      )}

      {/* Form — name and phone */}
      <form onSubmit={handleSubmit}>
        <div className="mb-2">
          <label className={labelClass}>Driver Name *</label>
          <input
            className={inputClass}
            placeholder="e.g. John Doe"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div>
          <label className={labelClass}>Driver Phone Number *</label>
          <input
            className={inputClass}
            placeholder="e.g. 9876543210"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="mt-5 w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition disabled:opacity-60 text-sm tracking-wide"
        >
          {loading ? "Saving…" : "+ Add Driver"}
        </button>
      </form>
    </div>
  );
};

// ── Main Export ───────────────────────────────────────────────────────────────
const AddVehicleDriver: React.FC = () => {
  return (
    <div className="w-full max-w-3xl mx-auto py-8 px-4 md:px-8 bg-slate-50 min-h-screen">
      <div className="mb-8 text-center">
        <h2 className="text-2xl font-bold text-slate-800 tracking-tight">Fleet Management</h2>
        <p className="text-sm text-slate-400 mt-1">Add vehicles and drivers independently</p>
      </div>
      <VehicleSection />
      <DriverSection />
    </div>
  );
};

export default AddVehicleDriver;